import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import DesktopSearch from 'home/components/SearchHero/DesktopSearch'
import MobileSearch from 'home/components/SearchHero/MobileSearch'
import Pane from 'components/Common/Pane'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import ToggleButton from 'LuxKit/ToggleButton'
import { useLocation } from 'react-router-dom'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import HeroImage from 'content/components/HeroImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import srOnly from 'styles/tools/sr-only'

const Wrapper = styled.div`
  ${mediaQueryDown.mobile} {
    &.Navbar {
      padding-bottom: ${rem(46)};
    }
  }
`

const LEGACY_TEXT_HERO_MOBILE_CONTENT_PADDING = rem(16)
const IMAGE_OVERLAY_PADDING_Y = rem(32)
const IMAGE_OVERLAY_PADDING_X = rem(32)
const IMAGE_OVERLAY_DESKTOP_PADDING_TOP = rem(98)
const MAX_IMAGE_OVERLAY_HERO_HEIGHT = rem(380)
const IMAGE_OVERLAY_CLASS_NAME = 'image-overlay'
const TEXT_MODE_CLASS_NAME = 'text-mode'

const HeroContainer = styled(HeroImage)`
  padding: 0;

  &.image-overlay {
    min-height: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    max-height: ${MAX_IMAGE_OVERLAY_HERO_HEIGHT};

    ${mediaQueryUp.tablet} {
      aspect-ratio: 4 / 1;
    }
  }
`

const HeroContent = styled(LayoutContainer)`
  position: relative;
  z-index: 3;

  &.${IMAGE_OVERLAY_CLASS_NAME} {
    ${mediaQueryDown.mobile} {
      padding: ${IMAGE_OVERLAY_PADDING_Y} ${IMAGE_OVERLAY_PADDING_X};
    }

    display: flex;
  }

  &.${IMAGE_OVERLAY_CLASS_NAME}.overlay-left {
    justify-content: flex-start;
  }

  &.${IMAGE_OVERLAY_CLASS_NAME}.overlay-center {
    justify-content: center;
  }

  &.${IMAGE_OVERLAY_CLASS_NAME}.overlay-right {
    justify-content: flex-end;
  }

  &.${TEXT_MODE_CLASS_NAME} {
    ${mediaQueryDown.mobile} {
      padding: ${LEGACY_TEXT_HERO_MOBILE_CONTENT_PADDING};
    }
  }
`

const ContentStack = styled(VerticalSpacer)`
  &.${IMAGE_OVERLAY_CLASS_NAME} {
    width: 100%;
  }

  &.${IMAGE_OVERLAY_CLASS_NAME}.overlay-left {
    text-align: left;
    align-items: flex-start;
  }

  &.${IMAGE_OVERLAY_CLASS_NAME}.overlay-center {
    text-align: center;
    align-items: center;
  }

  &.${IMAGE_OVERLAY_CLASS_NAME}.overlay-right {
    text-align: right;
    align-items: flex-end;
  }
`

const HeroDimmedOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));
  pointer-events: none;
  z-index: 1;

  &.${IMAGE_OVERLAY_CLASS_NAME} {
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.16), rgba(0, 0, 0, 0.16));
  }
`

const HeroOverlayImageWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 2;
`

const OverlayImageWrapper = styled(HeroOverlayImageWrapper)`
  top: ${IMAGE_OVERLAY_PADDING_Y};
  right: ${IMAGE_OVERLAY_PADDING_X};
  bottom: ${IMAGE_OVERLAY_PADDING_Y};
  left: ${IMAGE_OVERLAY_PADDING_X};

  &.${TEXT_MODE_CLASS_NAME} {
    ${mediaQueryUp.desktop} {
      top: ${IMAGE_OVERLAY_DESKTOP_PADDING_TOP};
    }
  }
`

const HeroOverlayImage = styled(ResponsiveImage).attrs({
  objectFit: 'contain' as const,
})`
  display: block;
  height: 100%;
  width: 100%;

  &:before {
    background-size: contain;
  }

  img {
    display: block;
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
`

const GlobalDesktopSearchContainer = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: unset;
  }

  ${mediaQueryUp.desktop} {
    position: relative;
    top: ${rem(80)};
  }
`

const NavbarContainer = styled.div`
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

const GlobalMobileSearchContainer = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding: ${rem(12)};

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const Title = styled(Heading)`
  text-align: left;

  ${mediaQueryUp.desktop} {
    text-align: center;
  }

  &.${IMAGE_OVERLAY_CLASS_NAME} {
    ${srOnly}
    text-align: inherit;
  }
`

const Description = styled(BodyText)`
  text-align: left;

  ${mediaQueryUp.desktop} {
    text-align: center;
  }
`

const TabsCard = styled(Pane)`
  margin-top: ${rem(-32)};
  border-radius: ${rem(2)};
  padding: ${rem(12)} 0;
  position: relative;
  z-index: 1;
  border-radius: ${rem(2)};
`

const Tabs = styled(CardCarousel)`
  padding: 0 ${rem(12)};
`

const StyledBreakpoint = styled(CSSBreakpoint)`
  position: absolute;
  bottom: ${rem(-46)};
  width: 100%;
  z-index: 4;
`

function SearchHero({
  image,
  desktopImage,
  mobileImage,
  overlayImage,
  title,
  subtitle,
  mode,
  overlayPosition = 'center',
  toggle,
  navBar,
}: App.ComponentHeroImage) {
  const isImageOverlayMode = mode === 'image-overlay'
  const heroModeClassName = isImageOverlayMode ? IMAGE_OVERLAY_CLASS_NAME : TEXT_MODE_CLASS_NAME
  const overlayPositionClassName = `overlay-${overlayPosition}`
  const baseImageId = image?.id
  const desktopImageId = desktopImage?.id
  const mobileImageId = mobileImage?.id
  const hasImageOverlayIds = !!(desktopImageId && mobileImageId)
  const resolvedDesktopImageId = isImageOverlayMode ? (hasImageOverlayIds ? desktopImageId : undefined) : baseImageId
  const resolvedMobileImageId = isImageOverlayMode ? (hasImageOverlayIds ? mobileImageId : undefined) : undefined

  const hasContent = title && resolvedDesktopImageId
  const { pathname } = useLocation()

  return (
    <Wrapper className={clsx(toggle)}>
      {hasContent &&
        <HeroContainer
          alt={title}
          className={clsx({ [IMAGE_OVERLAY_CLASS_NAME]: isImageOverlayMode })}
          fit="center"
          objectFit="cover"
          quality="good"
          gravity="auto"
          id={resolvedDesktopImageId}
          desktopId={resolvedDesktopImageId}
          mobileId={resolvedMobileImageId}
          aspectRatio="10:1"
          tabletAspectRatio="4:1"
        >
          <HeroDimmedOverlay className={clsx({ [IMAGE_OVERLAY_CLASS_NAME]: isImageOverlayMode })} aria-hidden="true" role="presentation" />
          {overlayImage?.id && <OverlayImageWrapper
            className={heroModeClassName}
          >
            <HeroOverlayImage
              alt=""
              aria-hidden="true"
              role="presentation"
              fit={overlayPosition}
              gravity="auto"
              quality="best"
              id={overlayImage.id}
            />
          </OverlayImageWrapper>}
          <HeroContent className={clsx(heroModeClassName, overlayPositionClassName)}>
            <ContentStack gap={20} className={clsx(heroModeClassName, overlayPositionClassName)}>
              <Title as={isImageOverlayMode ? 'h1' : undefined} colour="dark-contrast" variant="heading1" className={clsx('hero-heading', { [IMAGE_OVERLAY_CLASS_NAME]: isImageOverlayMode })}>
                {title}
              </Title>
              {!isImageOverlayMode && subtitle && <Description colour="dark-contrast" variant="large">{subtitle}</Description>}
              {toggle === 'Search Bar' && <>
                <GlobalMobileSearchContainer>
                  <MobileSearch />
                </GlobalMobileSearchContainer>
                <GlobalDesktopSearchContainer>
                  <DesktopSearch />
                </GlobalDesktopSearchContainer>
              </>}
            </ContentStack>
          </HeroContent>
          {toggle === 'Navbar' && <StyledBreakpoint min="tablet">
            <LayoutContainer className="navbar-container" size="sm">
              <NavbarContainer>
                <TabsCard paletteType="default">
                  <Tabs pageSize={navBar.length < 5 ? navBar.length : 'auto'} gap={0}>
                    {navBar.map((item) => <ToggleButton
                      kind="primary"
                      orientation="vertical"
                      key={`${item.title} - ${item.to}`}
                      title={item.title}
                      subtitle={item.subtitle}
                      to={item.to}
                      isSelected={item.to === pathname.slice(3)}
                    />)}
                  </Tabs>
                </TabsCard>
              </NavbarContainer>
            </LayoutContainer>
          </StyledBreakpoint>}
          {toggle === 'Navbar' && <StyledBreakpoint max="mobile">
            <NavbarContainer>
              <TabsCard paletteType="default">
                <Tabs gap={0}>
                  {navBar.map((item) => <ToggleButton
                    kind="primary"
                    orientation="vertical"
                    key={`${item.title} - ${item.to}`}
                    title={item.title}
                    subtitle={item.subtitle}
                    to={item.to}
                    isSelected={item.to === pathname.slice(3)}
                  />)}
                </Tabs>
              </TabsCard>
            </NavbarContainer>
          </StyledBreakpoint>}
        </HeroContainer>}
    </Wrapper>
  )
}

export default SearchHero
